import Main from "@/ts/base/Main";
import debounce from "debounce";
import FullscreenElement from "@/ts/base/Components/FullscreenElement";

export default class ParallaxScrollingElement {

	public container: HTMLElement;
	protected currentParallaxOffset: number;
	protected ease: number = ParallaxScrollingElement.DefaultEase;
	protected waypoint: HTMLElement;
	protected offsetTop: number = 0;
	protected headerEl: HTMLElement | null;
	protected headerComputedStyle: CSSStyleDeclaration | null = null;
	protected pictureEl: HTMLPictureElement | null;
	protected fullscreenParent: HTMLElement | null;
	protected _isVisible: boolean = false;

	static get DefaultEase(): number {
		return .33;
	}

	constructor(element: HTMLElement) {
		this.container = element;
		this.fullscreenParent = <HTMLElement | null>this.container.closest(".fullscreen");
		this.currentParallaxOffset = 0;

		let ease = this.container.getAttribute("data-ease");
		this.ease = ease !== null ? window.parseFloat(ease) : ParallaxScrollingElement.DefaultEase;
		this.waypoint = this.container.closest(".waypoint") || this.container;
		this.offsetTop = this.waypoint?.offsetTop || this.container.offsetTop;
		this.headerEl = Main.Instance.getHeaderComponent()?.getElement() || null;
		if (this.headerEl) {
			this.headerComputedStyle = window.getComputedStyle(this.headerEl);
		}
		this.pictureEl = this.container.querySelector("picture");

		if (this.pictureEl) {
			this.createHandlers();
		}
	}

	public get isVisible(): boolean {
		return this._isVisible;
	}

	public set isVisible(value: boolean) {
		this._isVisible = value;
	}

	adjustImageSize(): void {
		const imgEl = this.pictureEl?.querySelector("img");

		if (imgEl instanceof HTMLImageElement) {
			const containerRect = this.container.getBoundingClientRect();
			const imgRect = imgEl.getBoundingClientRect();
			const containerHeight = containerRect.height;
			const imgHeight = imgRect.height;
			const targetScrollHeight = (window.innerHeight - containerHeight - this.getHeaderHeightOffset()) * this.ease;
			if (imgHeight < containerHeight + targetScrollHeight) {
				const diff = (imgRect.height - containerRect.height);
				this.currentParallaxOffset = -Math.round(diff / 2);

				if (this.pictureEl) {
					this.pictureEl.style.height = (containerHeight + targetScrollHeight).toString() + "px";
					this.pictureEl.style.transform = `translateY(${this.currentParallaxOffset}px)`;
				}

			}
			else if (this.pictureEl) {
				this.pictureEl.style.height = "";
			}
		}
	}

	update(): void {
		const containerRect = this.container.getBoundingClientRect();
		const scrollY = Math.ceil(containerRect.top - this.getHeaderHeightOffset());

		this.currentParallaxOffset = Math.round(this.ease * scrollY) * -1;

		if (this.pictureEl) {
			this.pictureEl.style.transform = `translateY(${this.currentParallaxOffset}px)`;
		}

	}

	protected canAnimate(): boolean {
		return this._isVisible && this.canAdjustImageSize();
	}

	protected canAdjustImageSize(): boolean {
		return window.matchMedia("(min-width: 480px)").matches;
	}

	protected createHandlers(): void {
		window.addEventListener("scroll", this.onWindowScroll.bind(this), {
			passive: true
		});
		window.addEventListener("resize", debounce(this.onWindowResize.bind(this), 100, false));

		if (!this.fullscreenParent) {
			window.addEventListener(Main.IsLoadedClass, () => {
				if (this.canAdjustImageSize()) {
					this.adjustImageSize();
				}
			});
		}
		else {
			this.fullscreenParent.addEventListener(FullscreenElement.Resized, () => {
				if (this.canAdjustImageSize()) {
					this.adjustImageSize();
				}
			});
		}

	}


	protected getHeaderHeightOffset(): number {
		if (this.headerEl && this.headerComputedStyle && this.headerComputedStyle.position !== "fixed") {
			return this.headerEl.clientHeight;
		}

		return 0;
	}

	protected onWindowScroll(e: Event) {
		if (this.canAnimate()) {
			this.update();

			if (Main.Instance.scrollObserver.isFastScroll) {
				window.setTimeout(() => {
					this.update();
				}, 150);
			}
		}
	}

	protected onWindowResize(e: Event) {
		if (this.headerEl) {
			this.headerComputedStyle = window.getComputedStyle(this.headerEl);
		}
		this.update();
	}
}
